import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCommunityData } from "@/contexts/DataContext";
import { COOKIE_KEYS, deleteCookie, getCookie, setCookie } from "@/utils/cookies";
import { compareVersions, sortVersionsDescending } from "@/utils/version";

const CHANGELOG_PATH = "/changelog.json";
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

type ChangeType = "added" | "fixed" | "improved";

type ChangelogItem = {
  type: ChangeType;
  description: string;
  page?: string;
  demoGifPath?: string;
};

type VersionChanges = {
  version: string;
  date: string;
  changes: ChangelogItem[];
};

type ChangelogResponse = {
  currentVersion: string;
  changes: VersionChanges[];
};

type OpenMode = "manual" | "auto";

type ChangelogContextValue = {
  loading: boolean;
  error: string | null;
  isOpen: boolean;
  manualMode: boolean;
  visibleChanges: VersionChanges[];
  currentVersion?: string;
  hasUpdates: boolean;
  hasSessionUpdates: boolean;
  openChangelog: (mode?: OpenMode) => void;
  closeChangelog: () => void;
  acknowledgeCurrentVersion: () => void;
};

const filterChangesAfterVersion = (
  entries: VersionChanges[],
  referenceVersion?: string
): VersionChanges[] => {
  if (!referenceVersion) {
    return [];
  }

  return entries.filter((entry) => compareVersions(entry.version, referenceVersion) > 0);
};

const ChangelogContext = createContext<ChangelogContextValue | undefined>(undefined);

export const useChangelog = (): ChangelogContextValue => {
  const context = useContext(ChangelogContext);
  if (!context) {
    throw new Error("useChangelog must be used within a ChangelogProvider");
  }
  return context;
};

export const ChangelogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [manifest, setManifest] = useState<ChangelogResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [manualMode, setManualMode] = useState<boolean>(false);
  const [visibleChanges, setVisibleChanges] = useState<VersionChanges[]>([]);
  const [pendingManualOpen, setPendingManualOpen] = useState<boolean>(false);
  const [sessionUpdates, setSessionUpdates] = useState<VersionChanges[]>([]);

  const initialHasLoggedIn = getCookie(COOKIE_KEYS.CHANGELOG_HAS_LOGGED_IN) === "1";
  const [lastSeenVersion, setLastSeenVersion] = useState<string | undefined>(() =>
    getCookie(COOKIE_KEYS.CHANGELOG_LAST_SEEN)
  );
  const [suppressAuto, setSuppressAuto] = useState<boolean>(() =>
    getCookie(COOKIE_KEYS.CHANGELOG_SUPPRESS) === "1"
  );
  const [hasAuthenticatedBefore, setHasAuthenticatedBefore] = useState<boolean>(
    initialHasLoggedIn
  );

  const autoShownVersionRef = useRef<string | null>(null);
  const skipAutoOpenForSessionRef = useRef<boolean>(!initialHasLoggedIn);
  const { currentUser, isAuthenticated } = useAuth();
  const { communities } = useCommunityData();

  const changelogEnabled = useMemo(() => {
    const communityId = currentUser?.communityId;
    if (!communityId) {
      return true;
    }

    const community = communities.find(entry => entry.id === communityId);
    if (!community) {
      return true;
    }

    return community.showUpdates !== false;
  }, [communities, currentUser?.communityId]);

  useEffect(() => {
    const controller = new AbortController();

    const loadManifest = async () => {
      setLoading(true);
      try {
        const response = await fetch(CHANGELOG_PATH, {
          credentials: "same-origin",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`No se pudo cargar el changelog (${response.status})`);
        }

        const payload = (await response.json()) as ChangelogResponse;
        setManifest({ ...payload, changes: sortVersionsDescending(payload.changes) });
        setError(null);
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") {
          return;
        }
        setError(err instanceof Error ? err.message : "No se pudo cargar el changelog");
      } finally {
        setLoading(false);
      }
    };

    loadManifest();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      autoShownVersionRef.current = null;
      setIsOpen(false);
      setSessionUpdates([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (changelogEnabled) {
      return;
    }

    setIsOpen(false);
    setManualMode(false);
    setSessionUpdates([]);
    setPendingManualOpen(false);
  }, [changelogEnabled]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const hasLoggedCookie = getCookie(COOKIE_KEYS.CHANGELOG_HAS_LOGGED_IN) === "1";
    if (hasLoggedCookie && !hasAuthenticatedBefore) {
      setHasAuthenticatedBefore(true);
    }
  }, [hasAuthenticatedBefore, isAuthenticated]);

  const unseenChanges = useMemo(() => {
    if (!manifest || !lastSeenVersion || !changelogEnabled) {
      return [];
    }

    return filterChangesAfterVersion(manifest.changes, lastSeenVersion);
  }, [changelogEnabled, manifest, lastSeenVersion]);

  const openChangelog = useCallback(
    (mode: OpenMode = "manual") => {
      if (!changelogEnabled) {
        return;
      }

      if (!manifest) {
        if (mode === "manual") {
          setManualMode(true);
          setVisibleChanges([]);
          setPendingManualOpen(true);
          setIsOpen(true);
        }
        return;
      }

      if (!lastSeenVersion) {
        return;
      }

      if (unseenChanges.length > 0) {
        setManualMode(mode === "manual");
        setVisibleChanges(unseenChanges);
        setSessionUpdates(unseenChanges);
        setPendingManualOpen(false);
        setIsOpen(true);
        return;
      }

      if (mode === "manual" && sessionUpdates.length > 0) {
        setManualMode(true);
        setVisibleChanges(sessionUpdates);
        setPendingManualOpen(false);
        setIsOpen(true);
      }
    },
    [changelogEnabled, lastSeenVersion, manifest, sessionUpdates, unseenChanges]
  );

  const closeChangelog = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (
      !manifest ||
      isOpen ||
      !isAuthenticated ||
      !hasAuthenticatedBefore ||
      suppressAuto ||
      !changelogEnabled
    ) {
      return;
    }

    if (skipAutoOpenForSessionRef.current) {
      skipAutoOpenForSessionRef.current = false;
      return;
    }

    if (!lastSeenVersion) {
      return;
    }

    if (autoShownVersionRef.current === manifest.currentVersion) {
      return;
    }

    if (unseenChanges.length === 0) {
      return;
    }

    autoShownVersionRef.current = manifest.currentVersion;
    setManualMode(false);
    setVisibleChanges(unseenChanges);
    setSessionUpdates(unseenChanges);
    setIsOpen(true);
  }, [
    changelogEnabled,
    hasAuthenticatedBefore,
    isAuthenticated,
    isOpen,
    lastSeenVersion,
    manifest,
    suppressAuto,
    unseenChanges,
  ]);

  useEffect(() => {
    if (!manifest || !pendingManualOpen || !changelogEnabled) {
      return;
    }

    if (!lastSeenVersion) {
      setVisibleChanges([]);
      setPendingManualOpen(false);
      return;
    }

    setVisibleChanges(unseenChanges);
    if (unseenChanges.length > 0) {
      setSessionUpdates(unseenChanges);
    }
    setPendingManualOpen(false);
  }, [changelogEnabled, lastSeenVersion, manifest, pendingManualOpen, unseenChanges]);

  const acknowledgeCurrentVersion = useCallback(() => {
    if (!manifest) {
      setIsOpen(false);
      return;
    }

    const versionToStore = manifest.currentVersion;
    setCookie(COOKIE_KEYS.CHANGELOG_LAST_SEEN, versionToStore, {
      maxAge: ONE_YEAR_IN_SECONDS,
      sameSite: "Lax",
    });
    setLastSeenVersion(versionToStore);
    autoShownVersionRef.current = versionToStore;

    if (suppressAuto) {
      setCookie(COOKIE_KEYS.CHANGELOG_SUPPRESS, "1", {
        maxAge: ONE_YEAR_IN_SECONDS,
        sameSite: "Lax",
      });
    } else {
      deleteCookie(COOKIE_KEYS.CHANGELOG_SUPPRESS, { path: "/" });
      setSuppressAuto(false);
    }

    setIsOpen(false);
  }, [manifest, suppressAuto]);

  useEffect(() => {
    if (!manifest || !hasAuthenticatedBefore || lastSeenVersion) {
      return;
    }

    setCookie(COOKIE_KEYS.CHANGELOG_LAST_SEEN, manifest.currentVersion, {
      maxAge: ONE_YEAR_IN_SECONDS,
      sameSite: "Lax",
    });
    setLastSeenVersion(manifest.currentVersion);
  }, [hasAuthenticatedBefore, lastSeenVersion, manifest]);

  const hasUpdates = changelogEnabled && hasAuthenticatedBefore && unseenChanges.length > 0;

  const hasSessionUpdates = useMemo(
    () => (changelogEnabled && hasUpdates) || (changelogEnabled && sessionUpdates.length > 0),
    [changelogEnabled, hasUpdates, sessionUpdates.length]
  );

  const contextValue = useMemo<ChangelogContextValue>(
    () => ({
      loading,
      error,
      isOpen,
      manualMode,
      visibleChanges,
      currentVersion: manifest?.currentVersion,
      hasUpdates,
      hasSessionUpdates,
      openChangelog,
      closeChangelog,
      acknowledgeCurrentVersion,
    }),
    [
      acknowledgeCurrentVersion,
      manifest?.currentVersion,
      error,
      hasUpdates,
      hasSessionUpdates,
      isOpen,
      loading,
      manualMode,
      openChangelog,
      visibleChanges,
      closeChangelog,
    ]
  );

  return <ChangelogContext.Provider value={contextValue}>{children}</ChangelogContext.Provider>;
};
